/**
 * The footprint policy as data (ADR-040, ADR-052): an ordered list of rules that says what collision a
 * prop frame gets, matched on the frame's name, group and tileset. The first matching rule wins, so a
 * list ends with a catch-all. Rules are plain data, so the editor, the compiler and the asset build read
 * one table whether it came from a game's adapter in code or from a served contract.
 */

/**
 * A prop frame as the footprint policy sees it (ADR-040).
 */
export interface PropFrameInfo {
  readonly tileset: string;
  readonly tile: number;
  readonly name: string;
  readonly group: string;
}

/**
 * What collision a prop frame gets: `auto` derives it from the art's base, `none` leaves the prop
 * walkable, and a band height in pixels overrides how far above the base the derivation reads.
 */
export type FootprintPolicy = "auto" | "none" | { readonly band: number };

/**
 * What a rule matches on. Every field present must be contained in the frame's corresponding text,
 * ignoring case; a match with no field matches every frame.
 */
export interface FootprintRuleMatch {
  readonly nameContains?: string;
  readonly groupContains?: string;
  readonly tilesetContains?: string;
}

/**
 * One rule of the policy.
 */
export interface FootprintRule {
  readonly match: FootprintRuleMatch;
  readonly policy: FootprintPolicy;
}

/**
 * The policy a rule list gives a frame.
 *
 * @param rules The rules, in order.
 * @param frame The frame.
 * @returns The first matching rule's policy, or `none` when no rule matches, so a frame outside the
 *   table stamps nothing.
 */
export function evaluateFootprintRules(rules: readonly FootprintRule[], frame: PropFrameInfo): FootprintPolicy {
  for (const rule of rules) {
    if (matches(rule.match, frame)) {
      return rule.policy;
    }
  }
  return "none";
}

/**
 * Whether a frame satisfies every field a match names.
 *
 * @param match The match.
 * @param frame The frame.
 * @returns True when every named field is contained in the frame's text, ignoring case.
 */
function matches(match: FootprintRuleMatch, frame: PropFrameInfo): boolean {
  return contains(frame.name, match.nameContains) && contains(frame.group, match.groupContains) && contains(frame.tileset, match.tilesetContains);
}

/**
 * Whether a text contains a needle, ignoring case.
 *
 * @param text The frame's text.
 * @param needle The rule's text, or undefined when the rule does not match on this field.
 * @returns True when the rule does not name this field or the text contains it.
 */
function contains(text: string, needle: string | undefined): boolean {
  return needle === undefined || text.toLowerCase().includes(needle.toLowerCase());
}

/**
 * The callable form of an adapter's footprint policy, for a caller that derives footprints frame by frame.
 *
 * @param adapter A game's adapter, or anything carrying its rule list.
 * @returns A function from a frame to its policy; every frame stamps nothing when the game declares no
 *   rules.
 */
export function footprintPolicyFor(adapter: { readonly footprintPolicy?: readonly FootprintRule[] }): (frame: PropFrameInfo) => FootprintPolicy {
  const rules = adapter.footprintPolicy ?? [];
  return (frame) => evaluateFootprintRules(rules, frame);
}

/**
 * Checks a rule list: every match field it names is non-empty text and every policy is `auto`, `none` or
 * a band of positive integer pixels.
 *
 * @param rules The rules.
 * @param path Path for error messages.
 * @throws Error naming the offending rule and field.
 */
export function validateFootprintRules(rules: readonly FootprintRule[], path: string): void {
  rules.forEach((rule, index) => {
    const at = `${path}[${index}]`;
    for (const field of ["nameContains", "groupContains", "tilesetContains"] as const) {
      const text = rule.match[field];
      if (text !== undefined && text === "") {
        throw new Error(`${at}.match.${field} must not be empty`);
      }
    }
    const policy = rule.policy;
    if (policy === "auto" || policy === "none") {
      return;
    }
    if (typeof policy !== "object" || policy === null || !("band" in policy)) {
      throw new Error(`${at}.policy must be "auto", "none" or a band`);
    }
    if (!Number.isInteger(policy.band) || policy.band < 1) {
      throw new Error(`${at}.policy.band must be a positive integer`);
    }
  });
}
